// 画面の大きさへの合わせ方：基準解像度に対する UI の拡大率を決め、html の文字の大きさ（＝1rem）にする。
// 作りは「スマホの作り」1つ。PC（縦長の窓・縦置きのモニターも）とタブレットの横向きでは、ゲームを縦長の帯（#stage、高さの 9:16）にして、
// 余裕があれば「左のメニュー｜帯｜右のチャット」の3列を真ん中に寄せる（html[data-frame="side"]、チャットが入らなければ "nav"。下のタブは出さない）。
// 左右の余りは背景の絵をぼかして敷く。
// CSS の px はビルド時に rem へ、画面の大きさの @media は html のクラス（mq-…）の条件へ直してある（ui-scale.postcss.ts）。
// そのクラスをここで「基準の座標（帯の幅・高さ ÷ 拡大率）」で判定して付け外しする。
// なので CSS も JS も「基準の大きさのときの px」で考えればよい。JS で画面の幅を見るときは designWidth() / isMobileLayout() を使う。
// くわしくは Documentation/UI_SCALING.md。
import { ROOT_PX } from '../../ui-scale.postcss';

/** 基準解像度：縦長（スマホ）と横長（PC・タブレット横）。
 *  縦長は幅で合わせる（高さは Safari のツールバー込みでも収まる 660 を下限の目安に） */
// w：スマホの基準の幅。420（＝390px の端末で 0.93 倍）にして文字と部品を少し小さく、画面に収まる量を増やす
const PORTRAIT = { w: 420, h: 660, min: 0.85, max: 2.4 };
/** PC・タブレット横の帯：幅は高さの 9:16（最小 360px）。拡大率は帯の大きさで縦長と同じ式 */
const COLUMN = { ratio: 9 / 16, minW: 360, maxTall: 16 / 9 };
/** PC の帯：拡大率の下限と、帯の幅の上限（基準の px） */
const DESK_MIN_SCALE = 0.75;
const DESK_MAX_W = 600;
/** 帯の左に並べるメニュー・右に並べるチャットの幅（基準の px）と間隔 */
const SIDE_NAV_W = 190;
const SIDE_CHAT_W = 340;
const GAP = 16;
/** スマホの横向きなど、横長でも低すぎる画面は縦長の作りのまま（拡大しない） */
const SHORT_LANDSCAPE_H = 500;
/** これより狭い（基準の座標で）ならスマホの作り。CSS の (max-width: 899px) と同じ */
export const MOBILE_MAX = 899;

let scale = 1;
/** 帯の幅（px）。縦長の画面では画面の幅そのもの */
let stageW = 0;
let stageH = 0;
type FrameKind = 'full' | 'column' | 'nav' | 'side';
let frame: FrameKind = 'full';
const listeners = new Set<() => void>();

interface Frame {
  frame: FrameKind;
  scale: number;
  /** 帯の幅・左端・高さ・上端（px） */
  w: number;
  x: number;
  h?: number;
  y?: number;
  navW: number;
  chatW: number;
}
function compute(w: number, h: number): Frame {
  // 指で触る端末の縦向き（スマホ・タブレット縦）：画面いっぱい
  if (coarse && w <= h) return { frame: 'full', scale: clamp(Math.min(w / PORTRAIT.w, h / PORTRAIT.h), PORTRAIT.min, PORTRAIT.max), w, x: 0, navW: 0, chatW: 0 };
  // スマホの横向き（指で触る・低い）：そのまま（縦向きの案内を出す）
  if (coarse && h < SHORT_LANDSCAPE_H) return { frame: 'full', scale: 1, w, x: 0, navW: 0, chatW: 0 };
  // それ以外（PC は縦長の窓・縦置きのモニターも、タブレットの横向きも）：縦長の帯
  const cw0 = Math.min(w, Math.max(COLUMN.minW, Math.round(h * COLUMN.ratio)));
  const fit = Math.min(cw0 / PORTRAIT.w, h / PORTRAIT.h);
  // タブレット（指で触る）：帯の大きさに合わせて拡大。PC：拡大しない（基準より小さい窓だけ縮める）＝ブラウザの拡大・縮小がそのまま効く。
  // 帯の幅は基準の座標で DESK_MAX_W まで（縦置きのモニターなどで帯が広がりすぎてPCの作りにならないように）
  const sc = coarse ? Math.max(PORTRAIT.min, fit) : clamp(fit, DESK_MIN_SCALE, 1);
  const cw = coarse ? cw0 : Math.min(cw0, Math.round(DESK_MAX_W * sc));
  // 高さは 9:16 まで（PC は窓の形・ブラウザによらず同じ見た目に。立ち絵などは帯の高さで大きさが決まるので）。それより高い窓では上下に背景を見せて真ん中に
  const bh = Math.min(h, Math.round(cw * COLUMN.maxTall));
  const by = Math.round((h - bh) / 2);
  const navW = Math.round(SIDE_NAV_W * sc);
  const chatW = Math.round(SIDE_CHAT_W * sc);
  // メニュー｜帯｜チャットの3列を真ん中に寄せる（目線があちこちに行かないように）。入らなければチャットを外し、それも入らなければ帯だけ（下のタブ）
  if (w >= navW + cw + chatW + GAP * 4) return { frame: 'side', scale: sc, w: cw, x: Math.round((w - navW - cw - chatW - GAP * 2) / 2 + navW + GAP), h: bh, y: by, navW, chatW };
  if (w >= navW + cw + GAP * 3) return { frame: 'nav', scale: sc, w: cw, x: Math.round((w - navW - cw - GAP) / 2 + navW + GAP), h: bh, y: by, navW, chatW: 0 };
  return { frame: 'column', scale: sc, w: cw, x: Math.round((w - cw) / 2), h: bh, y: by, navW: 0, chatW: 0 };
}
const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

let lastW = 0;
function apply() {
  const w = window.innerWidth;
  // 文字の入力中（ソフトキーボードで高さだけ縮む）は拡大率を変えない
  const a = document.activeElement;
  if (w === lastW && a && (a.tagName === 'INPUT' || a.tagName === 'TEXTAREA' || (a as HTMLElement).isContentEditable)) return;
  lastW = w;
  const f = compute(w, window.innerHeight);
  const next = Math.round(f.scale * 1000) / 1000;
  const changed = next !== scale || f.frame !== frame || f.w !== stageW;
  scale = next;
  stageW = f.w;
  stageH = f.h ?? window.innerHeight;
  frame = f.frame;
  const root = document.documentElement;
  root.style.fontSize = `${ROOT_PX * scale}px`;
  root.style.setProperty('--ui-scale', String(scale));
  if (frame === 'full') {
    delete root.dataset.frame;
    root.style.removeProperty('--stage-w');
    root.style.removeProperty('--stage-x');
    root.style.removeProperty('--stage-y');
    root.style.removeProperty('--stage-h');
    root.style.removeProperty('--side-chat-w');
    root.style.removeProperty('--side-nav-w');
  } else {
    root.dataset.frame = frame;
    root.style.setProperty('--stage-w', `${f.w}px`);
    root.style.setProperty('--stage-x', `${f.x}px`);
    root.style.setProperty('--stage-y', `${f.y ?? 0}px`);
    root.style.setProperty('--stage-h', `${stageH}px`);
    root.style.setProperty('--side-chat-w', `${f.chatW}px`);
    root.style.setProperty('--side-nav-w', `${f.navW}px`);
  }
  root.dataset.layout = isMobileLayout() ? 'mobile' : 'pc';
  applyMq(root);
  // スマホの横向き（指で触る・横長・低い）は縦向きにしてもらう（縦長の作りのゲームの定番）
  const sideways = coarse && w > window.innerHeight && window.innerHeight < SHORT_LANDSCAPE_H && !rotateDismissed;
  root.toggleAttribute('data-rotate', sideways);
  if (changed) listeners.forEach((f) => f());
}
const coarse = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
let rotateDismissed = false;
function rotateHint() {
  const el = document.createElement('div');
  el.className = 'rotate-hint';
  el.setAttribute('role', 'dialog');
  el.innerHTML = `<div class="rh-box"><i class="rh-phone" aria-hidden="true"></i><b>縦向きでお楽しみください</b><button type="button" class="btn small">このまま遊ぶ</button></div>`;
  el.querySelector('button')!.addEventListener('click', () => {
    rotateDismissed = true;
    document.documentElement.removeAttribute('data-rotate');
  });
  document.body.appendChild(el);
}

/** UI の拡大率（基準解像度に対して） */
export const uiScale = () => scale;
/** 基準の座標での画面の幅・高さ（CSS の @container ui と同じ物差し） */
export const designWidth = () => (stageW || window.innerWidth) / scale;
export const designHeight = () => (stageH || window.innerHeight) / scale;
/** 拡大率・作りが変わったあと、CSS の読み込みが遅れていても条件を付け直す */
export function refreshMq() {
  mqKeys = null;
  applyMq(document.documentElement);
}
/** スマホの作り（下のタブ・縦積み）か */
export const isMobileLayout = () => designWidth() <= MOBILE_MAX;
/** 帯の右にチャットを開いたまま並べているか */
export const sideChat = () => frame === 'side';
/** 帯の左にメニューを並べているか（下のタブは出さない） */
export const sideNav = () => frame === 'side' || frame === 'nav';
/** 基準の px を CSS の長さに（JS で style を書くとき。拡大率に合わせて伸び縮みする） */
export const rem = (px: number) => `${Number((px / ROOT_PX).toFixed(4))}rem`;
/** 拡大率・作りが変わったとき */
export function onViewportChange(f: () => void) {
  listeners.add(f);
  return () => listeners.delete(f);
}

/** CSS にある画面の大きさの条件（mq-…）。最初に一度スタイルシートから集める */
let mqKeys: string[] | null = null;
function collectMq(): string[] {
  const keys = new Set<string>();
  const walk = (rules: CSSRuleList) => {
    for (const r of Array.from(rules)) {
      const sel = (r as CSSStyleRule).selectorText;
      if (sel) for (const m of sel.matchAll(/\.(mq-[a-z0-9_-]+)/g)) keys.add(m[1]);
      const inner = (r as CSSGroupingRule).cssRules;
      if (inner) walk(inner);
    }
  };
  for (const sh of Array.from(document.styleSheets)) {
    try {
      walk(sh.cssRules);
    } catch {
      /* 別オリジン（フォント）は読めない */
    }
  }
  return [...keys];
}
/** mq-w-le-899_h-le-760 のような鍵を、基準の座標の幅・高さで判定 */
export function mqMatches(key: string, w = designWidth(), h = designHeight()): boolean {
  return key
    .slice(3)
    .split('_')
    .every((t) => {
      const [f, op, a, b] = t.split('-');
      if (f === 'or') return op === 'portrait' ? h >= w : w > h;
      const v = f === 'w' ? w : f === 'h' ? h : w / h;
      const lim = f === 'ar' ? Number(a) / Number(b) : Number(a.replace('p', '.'));
      return op === 'ge' ? v >= lim : v <= lim;
    });
}
function applyMq(root: HTMLElement) {
  if (!mqKeys || !mqKeys.length) mqKeys = collectMq();
  for (const k of mqKeys) root.classList.toggle(k, mqMatches(k));
}

export function initViewport() {
  rotateHint();
  apply();
  // スタイルシートの読み込みが遅れたときのために、読み終わったら条件を集め直す
  window.addEventListener('load', () => refreshMq());
  let raf = 0;
  const on = () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(apply);
  };
  window.addEventListener('resize', on);
  window.addEventListener('orientationchange', on);
}
